import {
  CLASSIC_2990_DEFAULTS,
  CLASSIC_2990_SMART_ADDRESS_PARSER_TOKENS,
  CLASSIC_2990_VALIDATION_MESSAGES,
  getClassic2990ExportGroupTitle
} from "./classic2990Texts";

export const EMPTY_DOC_2990_FIELDS = {
  lishcatRishumLocation: "",
  sellerFirstName: "",
  sellerLastName: "",
  sellerId: "",
  assetCity: "",
  assetStreet: "",
  assetStreetNumber: "",
  blockNum: "",
  subBlock: "",
  subSubBlock: "",
  assetZipcode: "",
  assetHouseNum: "",
  passPartMekarkein: "",
  passPartMekarkeinMone: "",
  passPartMekarkeinMechane: ""
};

export const DOC_2990_NUMERIC_FIELDS = new Set([
  "blockNum",
  "subBlock",
  "subSubBlock",
  "assetZipcode",
  "assetStreetNumber",
  "assetHouseNum",
  "passPartMekarkeinMone",
  "passPartMekarkeinMechane"
]);

export const DOC_2990_AUTOFILL_TARGET_FIELDS = [
  "blockNum",
  "subBlock",
  "assetCity",
  "assetStreet",
  "assetStreetNumber",
  "assetZipcode",
  "assetHouseNum"
];

export const normalizeFieldValue = (value) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

export const normalizeDocFieldValueForState = (fieldName, value) => {
  if (value === null || value === undefined || value === "") return "";
  if (!DOC_2990_NUMERIC_FIELDS.has(fieldName)) {
    return String(value);
  }

  const parsedNumber = Number(
    String(value)
      .replace(/,/g, "")
      .trim()
  );

  return Number.isFinite(parsedNumber) ? parsedNumber : "";
};

export const normalizeAddressToken = (value) => (
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[,'"]/g, "")
    .toLowerCase()
);

const CATALOG_ALIAS_SEPARATOR_PATTERN = /[-\u05BE‐‑‒–—―/()]+/u;

const normalizeCatalogMatchToken = (value) => (
  normalizeAddressToken(value)
    .replace(/[-\u05BE‐‑‒–—―/().\u05F3\u05F4]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
);

const getUniqueCatalogValues = (values = []) => {
  const seen = new Set();

  return values.filter((value) => {
    const normalizedValue = normalizeCatalogMatchToken(value);
    if (!normalizedValue || seen.has(normalizedValue)) return false;
    seen.add(normalizedValue);
    return true;
  });
};

const getCatalogAliases = (value) => (
  String(value || "")
    .split(CATALOG_ALIAS_SEPARATOR_PATTERN)
    .map(normalizeCatalogMatchToken)
    .filter(Boolean)
);

const findSafeCatalogMatch = (value, catalogValues = []) => {
  const normalizedValue = normalizeCatalogMatchToken(value);
  if (!normalizedValue) return value;

  const candidates = getUniqueCatalogValues(catalogValues).map((catalogValue) => ({
    catalogValue,
    normalizedValue: normalizeCatalogMatchToken(catalogValue)
  }));
  const exactMatch = candidates.find((candidate) => candidate.normalizedValue === normalizedValue);
  if (exactMatch) return exactMatch.catalogValue;

  const aliasMatches = candidates.filter((candidate) => (
    getCatalogAliases(candidate.catalogValue).includes(normalizedValue)
  ));

  return aliasMatches.length === 1
    ? aliasMatches[0].catalogValue
    : value;
};

export const resolveClassicCatalogAddress = (parsedAddress, addresses = []) => {
  if (!parsedAddress) return parsedAddress;

  const city = findSafeCatalogMatch(
    parsedAddress.city,
    addresses.map((address) => address?.cityName).filter(Boolean)
  );
  const street = findSafeCatalogMatch(
    parsedAddress.street,
    addresses
      .filter((address) => address?.cityName === city)
      .map((address) => address?.streetName)
      .filter(Boolean)
  );

  return {
    ...parsedAddress,
    city,
    street
  };
};

export const normalizeSmartAddressFragment = (value) => (
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/^[,./\s-]+|[,./\s-]+$/g, "")
);

const SMART_ADDRESS_APARTMENT_TOKEN_PATTERN = CLASSIC_2990_SMART_ADDRESS_PARSER_TOKENS.join("|");
const SMART_ADDRESS_REGEX = new RegExp(
  `^(?<street>.+?)\\s+(?<house>\\d+)(?:(?:\\s*\\/\\s*|\\s+(?:${SMART_ADDRESS_APARTMENT_TOKEN_PATTERN})\\s+)(?<apartment>\\d+))?(?:\\s*,\\s*|\\s+)(?<city>.+)$`,
  "u"
);
const GOVMAP_ADDRESS_TEXT_PATTERN = /^[\u0590-\u05FF0-9\s,.'"\u05F3\u05F4()-]+$/u;
const GOVMAP_HEBREW_ADDRESS_LETTER_PATTERN = /[\u0590-\u05FF]/u;
const GOVMAP_MAX_LOOKUP_ADDRESS_LENGTH = 120;
const GOVMAP_MAX_HOUSE_NUMBER = 9999;

export const parseSmartLookupAddress = (value) => {
  const trimmed = String(value || "").trim().replace(/\s+/g, " ");
  if (!trimmed) return null;

  const zipMatch = trimmed.match(/^(?<address>.+?)(?:\s*,?\s+(?<zip>\d{5,7}))$/u);
  const addressWithoutZip = zipMatch?.groups?.address ? zipMatch.groups.address : trimmed;
  const zipCode = zipMatch?.groups?.zip ? normalizeFieldValue(zipMatch.groups.zip).trim() : "";

  const match = addressWithoutZip.match(SMART_ADDRESS_REGEX);

  if (!match?.groups) return null;

  const street = normalizeSmartAddressFragment(match.groups.street);
  const city = normalizeSmartAddressFragment(match.groups.city);
  const houseNumber = normalizeFieldValue(match.groups.house).trim();
  const apartmentNumber = normalizeFieldValue(match.groups.apartment).trim();
  const houseBase = parseInt(houseNumber, 10);
  const apartmentBase = apartmentNumber ? parseInt(apartmentNumber, 10) : null;
  const lookupAddress = `${street} ${houseNumber}, ${city}`;

  if (!street || !city || !Number.isFinite(houseBase) || houseBase <= 0) return null;
  if (houseBase > GOVMAP_MAX_HOUSE_NUMBER) return null;
  if (/\d/.test(city)) return null;
  if (apartmentNumber && (!Number.isFinite(apartmentBase) || apartmentBase <= 0)) return null;
  if (lookupAddress.length > GOVMAP_MAX_LOOKUP_ADDRESS_LENGTH) return null;
  if (!GOVMAP_ADDRESS_TEXT_PATTERN.test(lookupAddress)) return null;
  if (
    !GOVMAP_HEBREW_ADDRESS_LETTER_PATTERN.test(street)
    || !GOVMAP_HEBREW_ADDRESS_LETTER_PATTERN.test(city)
  ) return null;

  return {
    street,
    houseNumber,
    city,
    apartmentNumber: apartmentNumber || "",
    zipCode: zipCode || "",
    lookupAddress
  };
};

export const getSmartAddressLookupKey = (parsedAddress) => {
  if (!parsedAddress) return "";
  return [
    normalizeAddressToken(parsedAddress.street),
    normalizeAddressToken(parsedAddress.houseNumber),
    normalizeAddressToken(parsedAddress.city),
    normalizeAddressToken(parsedAddress.apartmentNumber),
    normalizeAddressToken(parsedAddress.zipCode)
  ].join("|");
};

export const normalizeFetchedDocFields = (serverFields = {}) => (
  Object.keys(EMPTY_DOC_2990_FIELDS).reduce((acc, key) => {
    acc[key] = normalizeDocFieldValueForState(key, serverFields[key]);
    return acc;
  }, {})
);

const parseTransferredShareValue = (value) => {
  const parts = normalizeFieldValue(value)
    .split(/[\\/]/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length !== 2) return {};

  return {
    passPartMekarkeinMone: normalizeDocFieldValueForState("passPartMekarkeinMone", parts[0]),
    passPartMekarkeinMechane: normalizeDocFieldValueForState("passPartMekarkeinMechane", parts[1])
  };
};

export const buildTransferredShareValue = (docFields = {}) => {
  const numerator = normalizeFieldValue(docFields.passPartMekarkeinMone).trim();
  const denominator = normalizeFieldValue(docFields.passPartMekarkeinMechane).trim();
  if (!numerator || !denominator) return "";
  return `${numerator}/${denominator}`;
};

export const normalizeDocFieldsForPersistence = (docFields = {}) => {
  const normalizedFields = Object.keys(EMPTY_DOC_2990_FIELDS).reduce((acc, key) => {
    acc[key] = normalizeDocFieldValueForState(key, docFields[key]);
    return acc;
  }, {});

  return {
    ...normalizedFields,
    passPartMekarkein: buildTransferredShareValue(normalizedFields)
  };
};

export const getSeededDocFields = ({ serverFields = {}, haklaiDraftPreCalc = {} }) => {
  const normalizedServerFields = normalizeFetchedDocFields(serverFields);
  const legacyTransferredShare = parseTransferredShareValue(normalizedServerFields.passPartMekarkein);
  const parcelGush = normalizeDocFieldValueForState("blockNum", haklaiDraftPreCalc?.parcelGush);
  const parcelHelka = normalizeDocFieldValueForState("subBlock", haklaiDraftPreCalc?.parcelHelka);

  return {
    ...EMPTY_DOC_2990_FIELDS,
    ...normalizedServerFields,
    passPartMekarkeinMone:
      normalizedServerFields.passPartMekarkeinMone || legacyTransferredShare.passPartMekarkeinMone || "",
    passPartMekarkeinMechane:
      normalizedServerFields.passPartMekarkeinMechane || legacyTransferredShare.passPartMekarkeinMechane || "",
    lishcatRishumLocation:
      normalizedServerFields.lishcatRishumLocation || CLASSIC_2990_DEFAULTS.lishcatRishumLocation,
    blockNum: normalizedServerFields.blockNum || parcelGush,
    subBlock: normalizedServerFields.subBlock || parcelHelka
  };
};

export const buildPropertyAutofillPatch = ({ lot, parcel, resolvedAddress, parsedAddress }) => {
  const patch = {
    blockNum: normalizeDocFieldValueForState("blockNum", lot),
    subBlock: normalizeDocFieldValueForState("subBlock", parcel)
  };

  const effectiveAddress = resolvedAddress || parsedAddress;

  if (effectiveAddress) {
    patch.assetCity = normalizeDocFieldValueForState("assetCity", effectiveAddress.city);
    patch.assetStreet = normalizeDocFieldValueForState("assetStreet", effectiveAddress.street);
    patch.assetStreetNumber = normalizeDocFieldValueForState(
      "assetStreetNumber",
      effectiveAddress.houseNumber
    );
  }

  if (parsedAddress?.apartmentNumber) {
    patch.assetHouseNum = normalizeDocFieldValueForState(
      "assetHouseNum",
      parsedAddress.apartmentNumber
    );
  }

  if (parsedAddress?.zipCode) {
    patch.assetZipcode = normalizeDocFieldValueForState(
      "assetZipcode",
      parsedAddress.zipCode
    );
  }

  return patch;
};

export const getParcelPairCacheKey = (lot, parcel) => `${lot}-${parcel}`;

export const getClassic2990ExportGroups = (analyzedPreCalc) => {
  const parts = Array.isArray(analyzedPreCalc?.preCalcPartsData) ? analyzedPreCalc.preCalcPartsData : [];
  const seenPropertyKeys = new Set();

  return parts.map((part, displayIndex) => {
    const haklaiPropertyKey = part?.haklaiPropertyKey;
    if (typeof haklaiPropertyKey !== "string" || !haklaiPropertyKey.trim()) {
      throw new Error("Haklai Form 2990 property key is missing or blank");
    }
    if (haklaiPropertyKey.trim() !== haklaiPropertyKey) {
      throw new Error("Haklai Form 2990 property key contains surrounding whitespace");
    }
    if (seenPropertyKeys.has(haklaiPropertyKey)) {
      throw new Error(`Haklai Form 2990 property key is duplicated: ${haklaiPropertyKey}`);
    }
    seenPropertyKeys.add(haklaiPropertyKey);

    return {
      haklaiPropertyKey,
      displayIndex,
      title: String(part?.haklaiPropertyLabel || "").trim()
        || getClassic2990ExportGroupTitle(displayIndex, parts.length),
      needsMutav: part?.needsMutav === true,
      needsRegTax: part?.needsRegTax === true
    };
  });
};

const DOC_2990_REQUIRED_JSON_FIELDS = [
  ["blockNum", CLASSIC_2990_VALIDATION_MESSAGES.missingBlockNum],
  ["subBlock", CLASSIC_2990_VALIDATION_MESSAGES.missingSubBlock],
  ["assetStreet", CLASSIC_2990_VALIDATION_MESSAGES.missingAssetStreet],
  ["assetStreetNumber", CLASSIC_2990_VALIDATION_MESSAGES.missingAssetStreetNumber],
  ["lishcatRishumLocation", CLASSIC_2990_VALIDATION_MESSAGES.missingLishcatRishumLocation],
  ["sellerFirstName", CLASSIC_2990_VALIDATION_MESSAGES.missingSellerFirstName],
  ["sellerLastName", CLASSIC_2990_VALIDATION_MESSAGES.missingSellerLastName],
  ["sellerId", CLASSIC_2990_VALIDATION_MESSAGES.missingSellerId],
  ["passPartMekarkeinMone", CLASSIC_2990_VALIDATION_MESSAGES.missingFractionNumerator],
  ["passPartMekarkeinMechane", CLASSIC_2990_VALIDATION_MESSAGES.missingFractionDenominator]
];

const hasRequiredJsonValue = (value) => (
  value !== null && value !== undefined && String(value).trim() !== ""
);

const buildJsonValidationResult = (message = "", fieldErrors = {}) => {
  const firstFieldName = Object.keys(fieldErrors)[0] || "";
  return {
    message,
    fieldErrors,
    firstFieldName,
    messages: Object.values(fieldErrors)
  };
};

const isIntegerValue = (value) => {
  if (!hasRequiredJsonValue(value)) return false;
  const parsed = Number(value);
  return Number.isFinite(parsed) && Number.isInteger(parsed);
};

export const validate2990JsonFieldsDetailed = (docFields = {}) => {
  const fieldErrors = {};

  DOC_2990_REQUIRED_JSON_FIELDS.forEach(([fieldName, message]) => {
    if (!hasRequiredJsonValue(docFields[fieldName])) fieldErrors[fieldName] = message;
  });

  if (
    hasRequiredJsonValue(docFields.sellerId)
    && String(docFields.sellerId).trim().length < 9
  ) {
    fieldErrors.sellerId = CLASSIC_2990_VALIDATION_MESSAGES.invalidSellerId;
  }

  const subBlockNum = Number(docFields.subBlock);
  if (
    hasRequiredJsonValue(docFields.subBlock)
    && (!Number.isFinite(subBlockNum) || subBlockNum > 9999 || subBlockNum < 0)
  ) {
    fieldErrors.subBlock = CLASSIC_2990_VALIDATION_MESSAGES.parcelRange;
  }

  if (
    hasRequiredJsonValue(docFields.passPartMekarkeinMone)
    && !isIntegerValue(docFields.passPartMekarkeinMone)
  ) {
    fieldErrors.passPartMekarkeinMone = CLASSIC_2990_VALIDATION_MESSAGES.fractionNumeratorMustUseInteger;
  }

  if (
    hasRequiredJsonValue(docFields.passPartMekarkeinMechane)
    && !isIntegerValue(docFields.passPartMekarkeinMechane)
  ) {
    fieldErrors.passPartMekarkeinMechane = CLASSIC_2990_VALIDATION_MESSAGES.fractionDenominatorMustUseInteger;
  }

  if (
    isIntegerValue(docFields.passPartMekarkeinMone)
    && Number(docFields.passPartMekarkeinMone) <= 0
  ) {
    fieldErrors.passPartMekarkeinMone = CLASSIC_2990_VALIDATION_MESSAGES.fractionNumeratorMustBePositive;
  }

  if (
    isIntegerValue(docFields.passPartMekarkeinMechane)
    && Number(docFields.passPartMekarkeinMechane) <= 0
  ) {
    fieldErrors.passPartMekarkeinMechane = CLASSIC_2990_VALIDATION_MESSAGES.fractionDenominatorMustBePositive;
  }

  if (Object.keys(fieldErrors).length) {
    return buildJsonValidationResult(CLASSIC_2990_VALIDATION_MESSAGES.summary, fieldErrors);
  }

  return buildJsonValidationResult();
};

export const validate2990JsonFields = (docFields) => {
  const validationResult = validate2990JsonFieldsDetailed(docFields);
  return validationResult.messages[0] || "";
};
